import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Plus, RefreshCw, Rss, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fetcher } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  name: "", url: "", outlet_ids: [], keywords: [], max_items: 5, interval_minutes: 60,
  active: true,
};

export default function Fuentes() {
  const { data: sources, mutate } = useSWR("/sources", fetcher, { refreshInterval: 30000 });
  const { data: outlets } = useSWR("/outlets", fetcher);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [keywordText, setKeywordText] = useState("");
  const { user } = useAuth();
  const canEdit = ["admin", "editor"].includes(user?.role);

  const open = (source) => {
    const base = source || EMPTY;
    setForm({ ...base });
    setKeywordText((base.keywords || []).join(", "));
  };

  const save = async () => {
    if (!form.name || !form.url) return toast.error("Indique nombre y enlace de la fuente");
    if (!(form.outlet_ids || []).length) return toast.error("Elija al menos un medio de destino");
    setBusy("save");
    const body = {
      name: form.name, url: form.url, outlet_ids: form.outlet_ids,
      keywords: keywordText.split(",").map((k) => k.trim()).filter(Boolean),
      max_items: Number(form.max_items) || 5,
      interval_minutes: Number(form.interval_minutes) || 60,
      active: form.active,
    };
    try {
      form.id ? await api.put(`/sources/${form.id}`, body) : await api.post("/sources", body);
      toast.success(form.id ? "Fuente actualizada" : "Fuente vigilada creada");
      setForm(null);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sources/${id}`);
      mutate();
      toast.success("Fuente eliminada");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const scan = async (source) => {
    setBusy(`scan-${source.id}`);
    try {
      const { data } = await api.post(`/sources/${source.id}/scan`);
      if (data.error) toast.error(`${source.name}: ${data.error}`);
      else toast.success(`${source.name}: ${data.new} nota(s) nueva(s) en la bandeja`);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const scanAll = async () => {
    setBusy("all");
    try {
      const { data } = await api.post("/sources/scan-all");
      const total = (data.results || []).reduce((n, r) => n + (r.new || 0), 0);
      toast.success(`${total} nota(s) nueva(s) de ${(data.results || []).length} fuente(s)`);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const toggleOutlet = (id) =>
    setForm((f) => ({
      ...f,
      outlet_ids: (f.outlet_ids || []).includes(id)
        ? f.outlet_ids.filter((x) => x !== id)
        : [...(f.outlet_ids || []), id],
    }));

  const outletName = (id) => (outlets || []).find((o) => o.id === id)?.name || id;

  return (
    <div data-testid="fuentes-page">
      <PageHeader
        kicker="Vigilancia"
        title="Fuentes vigiladas"
        subtitle="Medios y boletines que se revisan solos: las notas nuevas de tus temas llegan a la bandeja de ingesta."
      >
        <Button variant="outline" onClick={scanAll} disabled={busy === "all"} data-testid="scan-all-btn">
          <RefreshCw className="h-4 w-4 mr-2" />{busy === "all" ? "Revisando…" : "Revisar todas"}
        </Button>
        {canEdit && (
          <Button onClick={() => open(null)} data-testid="new-source-btn">
            <Plus className="h-4 w-4 mr-2" />Nueva fuente
          </Button>
        )}
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-2">
        {(sources || []).map((s) => (
          <div key={s.id} className="border border-border p-5 bg-card" data-testid={`source-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Rss className="h-4 w-4 text-primary shrink-0" />
                  <h3 className="font-bold truncate">{s.name}</h3>
                  {!s.active && <Badge variant="secondary">Pausada</Badge>}
                </div>
                <a href={s.url} target="_blank" rel="noreferrer"
                   className="text-xs mono text-muted-foreground hover:text-primary break-all">
                  {(s.feed_url || s.url).startsWith("scrape:")
                    ? `${s.url} · portada (sin RSS)`
                    : (s.feed_url || s.url)}
                </a>
              </div>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => remove(s.id)}
                        data-testid={`delete-source-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(s.outlet_ids || []).map((id) => (
                <Badge key={id} variant="default">{outletName(id)}</Badge>
              ))}
              {(s.keywords || []).map((k) => (
                <Badge key={k} variant="outline">{k}</Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Cada {s.interval_minutes} min · hasta {s.max_items} notas · {s.found_total || 0} captadas
              {s.last_run ? ` · última revisión ${s.last_run.slice(0, 16).replace("T", " ")}` : ""}
            </p>
            {s.last_status && (
              <p className="text-xs mono text-muted-foreground mt-1" data-testid={`status-${s.id}`}>
                {s.last_status}
              </p>
            )}
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={() => scan(s)} disabled={busy === `scan-${s.id}`}
                      data-testid={`scan-source-${s.id}`}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                {busy === `scan-${s.id}` ? "Revisando…" : "Revisar ahora"}
              </Button>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => open(s)}
                        data-testid={`edit-source-${s.id}`}>
                  Configurar
                </Button>
              )}
            </div>
          </div>
        ))}
        {sources && !sources.length && (
          <p className="text-sm text-muted-foreground" data-testid="sources-empty">
            Aún no hay fuentes vigiladas. Agregue el sitio o el feed de un medio y elija a qué medio
            de la agencia se destinan sus notas.
          </p>
        )}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="source-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">
              {form?.id ? "Configurar fuente" : "Nueva fuente vigilada"}
            </DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="Ministerio de Trabajo" className="mt-1.5"
                       data-testid="source-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Sitio o feed RSS</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                       placeholder="https://www.mt.gob.do/ o .../feed" className="mt-1.5"
                       data-testid="source-url-input" />
                <p className="text-xs text-muted-foreground mt-1">
                  Puede pegar la portada del medio: se busca el feed automáticamente.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Medios de destino</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(outlets || []).map((o) => {
                    const on = (form.outlet_ids || []).includes(o.id);
                    return (
                      <button key={o.id} type="button" onClick={() => toggleOutlet(o.id)}
                        className={`text-sm px-3 py-1.5 border transition-colors ${on
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-muted-foreground hover:border-primary/60"}`}
                        data-testid={`source-outlet-${o.id}`}>
                        {o.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Palabras clave (opcional)</Label>
                <Input value={keywordText} onChange={(e) => setKeywordText(e.target.value)}
                       placeholder="pensiones, seguridad social, CNSS" className="mt-1.5"
                       data-testid="source-keywords-input" />
                <p className="text-xs text-muted-foreground mt-1">
                  Separadas por coma. Si se deja vacío entran todas las notas nuevas.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Notas por revisión</Label>
                  <Input type="number" min="1" max="20" value={form.max_items}
                         onChange={(e) => setForm({ ...form, max_items: e.target.value })}
                         className="mt-1.5" data-testid="source-max-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Revisar cada (min)</Label>
                  <Input type="number" min="10" max="1440" value={form.interval_minutes}
                         onChange={(e) => setForm({ ...form, interval_minutes: e.target.value })}
                         className="mt-1.5" data-testid="source-interval-input" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!form.active}
                        onCheckedChange={(v) => setForm({ ...form, active: v })}
                        data-testid="source-active-switch" />
                <span className="text-sm">Vigilancia activa</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button onClick={save} disabled={busy === "save"} data-testid="save-source-btn">
              {busy === "save" ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
